/* eslint-disable no-console */
/**
 * Solomon Panel Context
 *
 * Global state management for the floating Solomon AI panel.
 * Mounted at app root to persist across page navigation.
 *
 * Refactored to compose focused hooks following Unix philosophy:
 * - usePanelUIState: Panel collapsed/expanded, position
 * - useSessionState: Session ID, mode, agent state
 * - useSlotSelection: Image slot selection flow
 * - useImageCallbacks: Callback registration for image actions
 */

import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import { useImageCallbacks } from 'hooks/solomon/useImageCallbacks';
import { useImageUpload, type ImageSlotType } from 'hooks/solomon/useImageUpload';
import { usePanelUIState } from 'hooks/solomon/usePanelUIState';
import { useSessionState } from 'hooks/solomon/useSessionState';
import { useSlotSelection } from 'hooks/solomon/useSlotSelection';
import { useSolomonAcceptAction } from 'hooks/solomon/useSolomonAcceptAction';
import { useSolomonExecuteAction } from 'hooks/solomon/useSolomonExecuteAction';
import { useSolomonRecommendations } from 'hooks/solomon/useSolomonRecommendations';
import solomonService from 'services/solomonService';

import { useImageGenerationFlow } from '../hooks/useImageGenerationFlow';
import type {
  SolomonPanelContextValue,
  PanelState,
  PanelPosition,
  SolomonPageContext,
  SolomonRecommendation,
  RecommendationStatus,
  ImageGenerationResult,
  // Unified agent types
  SolomonSubPage,
  SolomonAction,
  SolomonTypedPageData,
} from '../types';

// ============================================================================
// Context
// ============================================================================

const SolomonPanelContext = createContext<SolomonPanelContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface SolomonPanelProviderProps {
  children: ReactNode;
  /** Default panel state */
  defaultState?: PanelState;
  /** Default panel position */
  defaultPosition?: PanelPosition;
}

// ============================================================================
// Provider Component
// ============================================================================

export function SolomonPanelProvider({
  children,
  defaultState = 'collapsed',
  defaultPosition = 'right',
}: SolomonPanelProviderProps) {
  // -------------------------------------------------------------------------
  // Composed Hooks (Unix Philosophy - each hook does one thing well)
  // -------------------------------------------------------------------------

  // Panel UI state (collapsed/expanded, position)
  const panelUI = usePanelUIState({
    defaultState,
    defaultPosition,
    persist: false, // No localStorage persistence for now
  });

  // Session state (sessionId, mode, agentState)
  const session = useSessionState({
    initialMode: 'recommendations',
    initialAgentState: 'idle',
  });

  // -------------------------------------------------------------------------
  // Page Context State (original)
  // -------------------------------------------------------------------------
  const [pageContext, setPageContextState] = useState<SolomonPageContext | null>(null);
  const [currentState, setCurrentState] = useState<Record<string, unknown>>({});
  const [artistId, setArtistId] = useState<string | null>(null);
  const [artistName, setArtistName] = useState<string | null>(null);

  // Image generation flow (uses artistName for prompts)
  const imageGenFlow = useImageGenerationFlow(artistName || '');

  // Extended page context state (unified agent)
  const [subPage, setSubPage] = useState<SolomonSubPage | null>(null);
  const [pageData, setPageData] = useState<SolomonTypedPageData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  // Entity context - tells Solomon what specific item is being viewed
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<'episode' | 'series' | 'segment' | 'campaign' | 'workflow' | null>(null);

  // -------------------------------------------------------------------------
  // Pending Approvals (managed separately - will migrate to usePendingApprovals)
  // -------------------------------------------------------------------------
  const [pendingApprovals, setPendingApprovals] = useState<SolomonAction[]>([]);

  // Local recommendation states (for UI state that doesn't persist to server)
  const [localStates, setLocalStates] = useState<Record<string, RecommendationStatus>>({});

  // Active preview state
  const [activePreview, setActivePreview] = useState<{
    recommendation: SolomonRecommendation;
    result: ImageGenerationResult;
  } | null>(null);

  // Image callbacks hook (manages recommendation and direct tool use callbacks)
  const imageCallbacks = useImageCallbacks();

  // Image upload hook (uploads to server, returns permanent URLs)
  const imageUploadHook = useImageUpload({
    artistId,
    onSuccess: (imageUrl, slotType) => {
      console.log('[SolomonPanelContext] Image uploaded successfully', { imageUrl, slotType });
      // Notify ProfileImagesSection with permanent URL
      imageCallbacks.notifyImageApplied(slotType, imageUrl, null);
    },
    onError: (error) => {
      console.error('[SolomonPanelContext] Image upload failed:', error);
    },
  });

  // Slot selection hook (manages image slot selection flow)
  // On complete, upload the image and notify with permanent URL
  const slotSelectionHook = useSlotSelection({
    onComplete: async (slot, file) => {
      console.log('[SolomonPanelContext] Slot selection complete, uploading image', { slot, fileName: file.name });
      // Upload and get permanent URL (notification happens via onSuccess callback)
      await imageUploadHook.upload(file, slot as ImageSlotType);
    },
  });

  // Map to store executing recommendations by ID (supports concurrent executions)
  const executingRecommendationsRef = useRef<Map<string, SolomonRecommendation>>(new Map());

  // Recommendations are no longer auto-fetched - user initiates via chat
  // Keeping the hook structure for potential future use, but always disabled
  const {
    data: recommendationsData,
    isLoading,
    error,
    refetch,
  } = useSolomonRecommendations({
    artistId: artistId || '',
    pageContext: pageContext || 'profile_images',
    currentState,
    artistName: artistName || undefined,
    enabled: false, // Disabled - Solomon Panel now uses user-initiated chat only
  });

  // Execute action using existing hook
  const { executeAction: executeActionMutation, isExecuting } = useSolomonExecuteAction({
    artistId: artistId || '',
    pageContext: pageContext || 'profile_images',
    onSuccess: (response, request) => {
      if (response.success && response.result) {
        const rec = executingRecommendationsRef.current.get(request.recommendationId);
        if (rec) {
          setActivePreview({
            recommendation: rec,
            result: response.result as ImageGenerationResult,
          });
          setLocalState(rec.id, 'preview');
          executingRecommendationsRef.current.delete(request.recommendationId);
        }
      }
    },
    onError: (err, request) => {
      setLocalState(request.recommendationId, 'failed');
      executingRecommendationsRef.current.delete(request.recommendationId);
    },
  });

  // Accept action and persist to backend
  const { acceptAction: acceptActionMutation, isAccepting } = useSolomonAcceptAction({
    artistId: artistId || '',
    pageContext: pageContext || 'profile_images',
    onSuccess: (response) => {
      if (response.success) {
        // Successfully persisted to backend
        console.log('[Solomon] Image persisted to database');
      }
    },
    onError: (error) => {
      console.error('[Solomon] Failed to persist image:', error);
    },
  });

  // Toggle panel state - delegated to panelUI hook
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const togglePanel = panelUI.toggle;

  // Set page context with all required data (original API)
  const setPageContext = useCallback(
    (ctx: SolomonPageContext, state: Record<string, unknown>, newArtistId: string, newArtistName?: string) => {
      setPageContextState(ctx);
      setCurrentState(state);
      setArtistId(newArtistId);
      setArtistName(newArtistName || null);
      // Reset local states when context changes
      setLocalStates({});
      setActivePreview(null);
    },
    []
  );

  // Set extended page context for unified agent
  const setExtendedPageContext = useCallback(
    (
      tab: SolomonPageContext,
      newSubPage: SolomonSubPage,
      newPageData: SolomonTypedPageData,
      newArtistId: string,
      newUserId: string,
      newArtistName?: string,
      newEntityId?: string,
      newEntityType?: 'episode' | 'series' | 'segment' | 'campaign' | 'workflow'
    ) => {
      setPageContextState(tab);
      setSubPage(newSubPage);
      setPageData(newPageData);
      setCurrentState(newPageData as Record<string, unknown>);
      setArtistId(newArtistId);
      setUserId(newUserId);
      setArtistName(newArtistName || null);
      // Set entity context - tells Solomon what specific item is being viewed
      setEntityId(newEntityId || null);
      setEntityType(newEntityType || null);
      // Reset states when context changes
      setLocalStates({});
      setActivePreview(null);
      setPendingApprovals([]);
    },
    []
  );

  // Add a pending action to the queue
  const addPendingApproval = useCallback((action: SolomonAction) => {
    setPendingApprovals((prev) => {
      if (prev.some((a) => a.id === action.id || a.approvalToken === action.approvalToken)) {
        return prev;
      }
      return [...prev, action];
    });
  }, []);

  // Approve a pending action
  const approveAction = useCallback(
    async (approvalToken: string) => {
      if (!artistId) {
        console.error('[Solomon] Cannot approve action without artistId');
        return;
      }

      console.log('[Solomon] Approving action:', approvalToken);
      try {
        const response = await solomonService.processApproval({
          artistId,
          approvalToken,
          approved: true,
          sessionId: session.sessionId || undefined,
        });

        if (response.success) {
          setPendingApprovals((prev) => prev.filter((a) => a.approvalToken !== approvalToken));
          console.log('[Solomon] Action approved successfully');
        } else {
          console.error('[Solomon] Approval failed:', response.error);
        }
      } catch (error) {
        console.error('[Solomon] Failed to approve action:', error);
      }
    },
    [artistId, session.sessionId]
  );

  // Reject a pending action
  const rejectPendingAction = useCallback(
    async (approvalToken: string, reason?: string) => {
      if (!artistId) {
        console.error('[Solomon] Cannot reject action without artistId');
        return;
      }

      console.log('[Solomon] Rejecting action:', approvalToken);
      try {
        const response = await solomonService.processApproval({
          artistId,
          approvalToken,
          approved: false,
          rejectionReason: reason,
          sessionId: session.sessionId || undefined,
        });

        if (response.success) {
          setPendingApprovals((prev) => prev.filter((a) => a.approvalToken !== approvalToken));
          console.log('[Solomon] Action rejected successfully');
        } else {
          console.error('[Solomon] Rejection failed:', response.error);
        }
      } catch (error) {
        console.error('[Solomon] Failed to reject action:', error);
      }
    },
    [artistId, session.sessionId]
  );

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Execute an action
  const executeAction = useCallback(
    async (recommendation: SolomonRecommendation, customPrompt?: string) => {
      console.log('[SolomonPanelContext] executeAction called:', {
        recommendationId: recommendation.id,
        actionType: recommendation.actionType,
      });

      setLocalState(recommendation.id, 'generating');

      // Store recommendation in map keyed by ID (supports concurrent executions)
      executingRecommendationsRef.current.set(recommendation.id, recommendation);

      try {
        const ctx = recommendation.context;
        await executeActionMutation({
          recommendationId: recommendation.id,
          actionType: recommendation.actionType,
          params: {
            prompt: customPrompt || (ctx.suggestedPrompt as string | undefined),
            size: ctx.targetAspectRatio as string | undefined,
            style: 'natural',
            referenceImages: ctx.referenceImageUrls as string[] | undefined,
          },
        });
      } catch {
        setLocalState(recommendation.id, 'failed');
        executingRecommendationsRef.current.delete(recommendation.id);
      }
    },
    [executeActionMutation]
  );

  // Accept an action result
  const acceptAction = useCallback(
    async (recommendation: SolomonRecommendation) => {
      console.log('[SolomonPanelContext] acceptAction called:', {
        recommendationId: recommendation.id,
        activePreviewId: activePreview?.recommendation.id,
        hasActivePreview: !!activePreview,
        idsMatch: activePreview?.recommendation.id === recommendation.id,
      });

      if (activePreview && activePreview.recommendation.id === recommendation.id) {
        console.log('[SolomonPanelContext] Condition passed - processing accept');
        setLocalState(recommendation.id, 'accepted');

        // Call the registered callback (for local UI updates like showing unsaved state)
        console.log('[SolomonPanelContext] Invoking callback via imageCallbacks hook');
        imageCallbacks.invokeActionCallback(recommendation, activePreview.result);

        // Get the target field from recommendation context
        const targetField = recommendation.context.imageKey as string;
        console.log('[SolomonPanelContext] Target field for persist:', targetField);

        // Persist to backend
        if (targetField && recommendation.actionType === 'generate_image') {
          try {
            await acceptActionMutation({
              recommendationId: recommendation.id,
              actionType: recommendation.actionType,
              result: activePreview.result,
              targetField,
            });
          } catch {
            // Revert optimistic state so UI reflects the failure
            setLocalState(recommendation.id, 'failed');
          }
        } else {
          console.log('[SolomonPanelContext] Skipping backend persist:', {
            targetField,
            actionType: recommendation.actionType,
          });
        }

        // Clear preview after a short delay
        console.log('[SolomonPanelContext] Clearing preview in 500ms');
        setTimeout(() => {
          console.log('[SolomonPanelContext] Clearing activePreview now');
          setActivePreview(null);
        }, 500);
      } else {
        console.error('[SolomonPanelContext] acceptAction condition FAILED:', {
          hasActivePreview: !!activePreview,
          recommendationId: recommendation.id,
          activePreviewRecId: activePreview?.recommendation?.id,
        });
      }
    },
    [activePreview, acceptActionMutation, imageCallbacks]
  );

  // Reject an action result
  const rejectAction = useCallback((recommendation: SolomonRecommendation) => {
    setLocalState(recommendation.id, 'rejected');
    setActivePreview(null);
  }, []);

  // Set local state for a recommendation
  const setLocalState = useCallback((recommendationId: string, status: RecommendationStatus) => {
    setLocalStates((prev) => ({
      ...prev,
      [recommendationId]: status,
    }));
  }, []);

  // Clear active preview
  const clearActivePreview = useCallback(() => {
    setActivePreview(null);
  }, []);

  // Memoized context value - composes focused hooks with backward-compatible API
  const contextValue = useMemo<SolomonPanelContextValue>(
    () => ({
      // Panel UI state (from usePanelUIState hook)
      panelState: panelUI.panelState,
      panelPosition: panelUI.panelPosition,
      setPanelState: panelUI.setPanelState,
      setPanelPosition: panelUI.setPanelPosition,
      togglePanel: panelUI.toggle,

      // Page context (original)
      pageContext,
      currentState,
      artistId,
      artistName,
      setPageContext,

      // Extended page context (unified agent)
      subPage,
      pageData,
      userId,
      entityId,
      entityType,
      setExtendedPageContext,

      // Recommendations
      recommendations: recommendationsData?.recommendations || [],
      insight: recommendationsData?.insight || null,
      isLoading: isLoading || isExecuting || isAccepting,
      isTyping: false, // Will be managed by InsightDisplay
      error: error as Error | null,

      // Actions (original)
      fetchRecommendations,
      executeAction,
      acceptAction,
      rejectAction,

      // Preview
      activePreview,
      setActivePreview,
      clearActivePreview,

      // Local states
      localStates,
      setLocalState,

      // Callback (recommendation flow) - from useImageCallbacks hook
      onActionApplied: imageCallbacks.actionCallbackRef.current,
      registerActionCallback: imageCallbacks.registerActionCallback,
      unregisterActionCallback: imageCallbacks.unregisterActionCallback,

      // Callback (direct tool use flow via SSE streaming) - from useImageCallbacks hook
      registerImageAppliedCallback: imageCallbacks.registerImageAppliedCallback,
      unregisterImageAppliedCallback: imageCallbacks.unregisterImageAppliedCallback,
      notifyImageApplied: imageCallbacks.notifyImageApplied,

      // Callback (save image direct) - from useImageCallbacks hook
      registerSaveImageDirectCallback: imageCallbacks.registerSaveImageDirectCallback,
      unregisterSaveImageDirectCallback: imageCallbacks.unregisterSaveImageDirectCallback,
      saveImageDirect: imageCallbacks.saveImageDirect,

      // Image generation flow
      imageGenFlow,

      // Slot selection flow - from useSlotSelection hook
      slotSelection: slotSelectionHook.slotSelection,
      startSlotSelection: slotSelectionHook.startSlotSelection,
      completeSlotSelection: slotSelectionHook.completeSlotSelection,
      cancelSlotSelection: slotSelectionHook.cancelSlotSelection,

      // Unified agent state (from useSessionState hook)
      mode: session.mode,
      setMode: session.setMode,
      agentState: session.agentState,
      sessionId: session.sessionId,
      pendingApprovals,
      addPendingApproval,
      approveAction,
      rejectPendingAction,
    }),
    [
      // Panel UI state (from composed hook)
      panelUI,
      // Page context (original)
      pageContext,
      currentState,
      artistId,
      artistName,
      setPageContext,
      // Extended page context (unified agent)
      subPage,
      pageData,
      userId,
      entityId,
      entityType,
      setExtendedPageContext,
      // Recommendations
      recommendationsData,
      isLoading,
      isExecuting,
      isAccepting,
      error,
      // Actions
      fetchRecommendations,
      executeAction,
      acceptAction,
      rejectAction,
      // Preview
      activePreview,
      clearActivePreview,
      // Local states
      localStates,
      setLocalState,
      // Image callbacks (from composed hook)
      imageCallbacks,
      // Image upload (from composed hook)
      imageUploadHook,
      // Image generation flow
      imageGenFlow,
      // Slot selection (from composed hook)
      slotSelectionHook,
      // Unified agent state (from composed hook)
      session,
      pendingApprovals,
      addPendingApproval,
      approveAction,
      rejectPendingAction,
    ]
  );

  return <SolomonPanelContext.Provider value={contextValue}>{children}</SolomonPanelContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useSolomonPanelContext(): SolomonPanelContextValue {
  const context = useContext(SolomonPanelContext);

  if (!context) {
    throw new Error('useSolomonPanelContext must be used within a SolomonPanelProvider');
  }

  return context;
}

// ============================================================================
// Optional Hook (returns null if not in provider)
// ============================================================================

export function useSolomonPanelContextOptional(): SolomonPanelContextValue | null {
  return useContext(SolomonPanelContext);
}

export default SolomonPanelContext;
